/**
 * Normalize filter/search params into a stable, structurally-hashable key segment.
 *
 * TanStack Query already hashes object *keys* deterministically (sorted), so two equal
 * filter objects always produce the same cache entry regardless of property order. What
 * it does NOT do is collapse "absent" vs "empty" filters, so we normalize VALUES here:
 *   - drop `undefined` / `null` / `''` so `{search:''}` and `{}` share one cache entry;
 *   - optionally sort arrays whose order is not semantic (e.g. a multi-select filter),
 *     so `['a','b']` and `['b','a']` hit the same entry.
 *
 * Returns `undefined` when nothing meaningful remains, so callers can emit a short key
 * (`['documents', auditId]`) that is a true PREFIX of the filtered variant — making
 * `invalidateQueries({ queryKey: ['documents', auditId] })` cover every filtered list.
 */
export const stableFilters = <T extends object>(
  params?: T,
  opts?: { sortArrays?: boolean },
): Partial<T> | undefined => {
  if (!params) return undefined;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    const arr = value as unknown[];
    out[key] = opts?.sortArrays && Array.isArray(value) ? [...arr].sort() : value;
  }
  return Object.keys(out).length ? (out as Partial<T>) : undefined;
};
